/**
 * MCP client setup snippets — single source of truth.
 *
 * Every snippet here was verified against that client's current documentation,
 * not written from memory. A wrong snippet is worse than no snippet: the user
 * cannot tell whether the failure is their token, our server, or our docs.
 *
 * Each `snippet(url, token)` renders with the real token when one is available
 * (immediately after minting, in the create dialog) and with TOKEN_PLACEHOLDER
 * otherwise (the permanent section on the page, where the token is by then
 * unrecoverable).
 */

export const TOKEN_PLACEHOLDER = "<YOUR_TOKEN>";

export interface McpClient {
  id: string;
  label: string;
  /** Syntax-highlight hint and, for JSON entries, the file the user edits. */
  lang: "bash" | "json";
  /** Where the snippet goes, shown above the code block. */
  target: string;
  snippet: (url: string, token: string) => string;
}

export const MCP_CLIENTS: readonly McpClient[] = [
  {
    id: "claude-code",
    label: "Claude Code",
    lang: "bash",
    target: "Run in your terminal",
    snippet: (url, token) =>
      `claude mcp add --transport http taitora ${url} \\\n  --header "Authorization: Bearer ${token}"`,
  },
  {
    id: "claude-desktop",
    label: "Claude Desktop",
    lang: "json",
    target: "claude_desktop_config.json",
    // The header is passed via env rather than inline because Claude Desktop on
    // Windows (and Cursor) fail to escape spaces inside args, which mangles
    // "Authorization: Bearer x". Keeping the space inside the env value is the
    // workaround mcp-remote documents.
    snippet: (url, token) =>
      JSON.stringify(
        {
          mcpServers: {
            taitora: {
              command: "npx",
              args: ["-y", "mcp-remote", url, "--header", "Authorization:${AUTH_HEADER}"],
              env: { AUTH_HEADER: `Bearer ${token}` },
            },
          },
        },
        null,
        2,
      ),
  },
  {
    id: "cursor",
    label: "Cursor",
    lang: "json",
    target: "~/.cursor/mcp.json",
    snippet: (url, token) =>
      JSON.stringify(
        {
          mcpServers: {
            taitora: { url, headers: { Authorization: `Bearer ${token}` } },
          },
        },
        null,
        2,
      ),
  },
  {
    id: "opencode",
    label: "opencode",
    lang: "json",
    target: "opencode.json",
    snippet: (url, token) =>
      JSON.stringify(
        {
          $schema: "https://opencode.ai/config.json",
          mcp: {
            taitora: {
              type: "remote",
              url,
              enabled: true,
              headers: { Authorization: `Bearer ${token}` },
            },
          },
        },
        null,
        2,
      ),
  },
  {
    id: "curl",
    label: "curl (test)",
    lang: "bash",
    target: "Check the token works before wiring up a client",
    // A real MCP handshake, not just a reachability ping: Streamable HTTP is a
    // single endpoint that dispatches on method, so a bare GET tells you almost
    // nothing. A successful initialize proves the token, the route, and the
    // transport all work. Accept must list both types — the server may answer
    // with either JSON or an event stream.
    snippet: (url, token) =>
      `curl -X POST ${url} \\\n` +
      `  -H "Authorization: Bearer ${token}" \\\n` +
      `  -H "Content-Type: application/json" \\\n` +
      `  -H "Accept: application/json, text/event-stream" \\\n` +
      `  -d '{"jsonrpc":"2.0","id":1,"method":"initialize","params":{` +
      `"protocolVersion":"2025-03-26","capabilities":{},` +
      `"clientInfo":{"name":"curl","version":"1.0"}}}'`,
  },
] as const;

export const DEFAULT_CLIENT_ID = MCP_CLIENTS[0]!.id;

/**
 * Expiry choices offered when minting a token. `days: 0` means never expires
 * (the API treats a zero or absent `expires_in_days` as no expiry).
 *
 * The 365 ceiling mirrors `maxExpiryDays` in internal/mcp/apikeys_handler.go —
 * the server rejects anything larger, so offering more here would only produce
 * a 400.
 */
export const TOKEN_EXPIRY_OPTIONS: readonly { label: string; days: number }[] = [
  { label: "Never expires", days: 0 },
  { label: "30 days", days: 30 },
  { label: "60 days", days: 60 },
  { label: "90 days", days: 90 },
  { label: "1 year", days: 365 },
] as const;

/** Renders a client's snippet, falling back to the placeholder when no token is in hand. */
export function renderSnippet(client: McpClient, url: string, token?: string): string {
  return client.snippet(url, token ?? TOKEN_PLACEHOLDER);
}
